import { Box, Button, Icon, Theme, Typography } from "@outschool/backpack";
import { faBellConcierge, faQuestionCircle } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useNavigation } from "@outschool/ui-utils";
import React, { useCallback } from "react";

import useConcierge from "../../hooks/useConcierge";
import { CONCIERGE_TRACKING_EVENTS } from "../concierge/constants";
import { ConciergeNavTab } from "./DesktopNavTabs";

interface ConciergeNavItemProps {
  minimalistic?: boolean;
  onClick?: () => void;
}

export const ConciergeNavItemDisplay = ({
  minimalistic = false,
  onClick,
}: ConciergeNavItemProps) => {
  const { t } = useTranslation("client\\components\\nav\\ConciergeNavItem");
  const { isLoggedIn } = useSession();

  const { canAccessConcierge } = useConcierge();
  if (isLoggedIn) {
    return <ConciergeNavTab onClick={onClick} />;
  }

  return (
    <Box
      onClick={onClick}
      sx={(theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
          marginY: theme.spacing(10),
        },
      })}
    >
      <Button
        variant="link"
        sx={(theme: Theme) => ({
          padding: minimalistic ? "0px" : "10px 10px",
          border: "none",
          outline: "none",
          position: "relative",
          borderRadius: "30px",
          cursor: "pointer",
          zIndex: "1",
          color: "black",
          backgroundColor: "transparent",
          ":hover:not(:disabled)": {
            textDecoration: "none",
          },
          [theme.breakpoints.down("md")]: {
            display: "flex",
            flexDirection: "column-reverse",
            paddingY: theme.spacing(14),
            paddingX: minimalistic ? theme.spacing(5) : theme.spacing(20),
          },
          [theme.breakpoints.between("md", "lg")]: {
            padding: 0,
          },
        })}
      >
        <Typography
          variant="body2"
          sx={(theme: Theme) => ({
            [theme.breakpoints.up("sm")]: {
              display: "none",
            },
          })}
        >
          {canAccessConcierge ? t("Concierge") : t("Help")}
        </Typography>
        {!minimalistic && (
          <Icon
            icon={canAccessConcierge ? faBellConcierge : faQuestionCircle}
            sx={{ fontSize: "2rem", marginRight: "5px" }}
          />
        )}
        <Typography
          emphasized
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("sm")]: {
              display: "none",
            },
          })}
        >
          {canAccessConcierge ? t("Concierge") : t("Help")}
        </Typography>
      </Button>
    </Box>
  );
};

export const ConciergeNavItemContainer = ({
  children,
}: {
  children: (props: { onClick: () => void }) => React.ReactElement;
}) => {
  const navigate = useNavigation();

  const locale = useLocale();

  const clickConciergeButton = useCallback(() => {
    const intercom = (window as any)?.Intercom;

    if (intercom) {
      try {
        intercom("show");
      } catch (_e) {
        navigate(`https://support.outschool.com/${locale}`, { newTab: true });
      }
    } else {
      navigate(`https://support.outschool.com/${locale}`, { newTab: true });
    }
  }, [locale, navigate]);

  return <>{children({ onClick: () => clickConciergeButton() })}</>;
};

export function ConciergeNavItem({
  minimalistic = false,
}: {
  minimalistic?: boolean;
}) {
  const trackEvent = useTrackEvent();
  const handleNavClick = () => {
    trackEvent(CONCIERGE_TRACKING_EVENTS.NAV_ITEM_CLICKED);
  };

  const { isLoggedIn } = useSession();
  if (!isLoggedIn) {
    return null;
  }

  return (
    <ConciergeNavItemContainer>
      {({ onClick }) => (
        <ConciergeNavItemDisplay
          minimalistic={minimalistic}
          onClick={() => {
            handleNavClick();
            onClick();
          }}
        />
      )}
    </ConciergeNavItemContainer>
  );
}
